import { supabase } from '@/integrations/supabase/client';

export const SUPPORT_ATTACHMENT_BUCKET = 'support-attachments';
export const SUPPORT_ATTACHMENT_MAX_SIZE = 5 * 1024 * 1024;
export const SUPPORT_ATTACHMENT_ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
].join(',');

type SupportAttachmentKind = 'image' | 'text';

type SupportAttachmentConfig = {
  kind: SupportAttachmentKind;
  extensions: string[];
};

const SUPPORT_ATTACHMENT_RULES: Record<string, SupportAttachmentConfig> = {
  'image/png': { kind: 'image', extensions: ['png'] },
  'image/jpeg': { kind: 'image', extensions: ['jpg', 'jpeg'] },
  'image/webp': { kind: 'image', extensions: ['webp'] },
  'image/gif': { kind: 'image', extensions: ['gif'] },
  'text/plain': { kind: 'text', extensions: ['txt', 'log'] },
  'text/markdown': { kind: 'text', extensions: ['md', 'markdown'] },
  'text/csv': { kind: 'text', extensions: ['csv'] },
  'application/json': { kind: 'text', extensions: ['json'] },
};

export interface SupportAttachmentSelection {
  file: File;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  kind: SupportAttachmentKind;
  previewUrl: string | null;
}

export interface SupportAttachmentUpload {
  path: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  kind: SupportAttachmentKind;
}

function getFileExtension(name: string) {
  const parts = name.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || '' : '';
}

function sanitizeFileStem(name: string) {
  const extension = getFileExtension(name);
  const withoutExtension = extension ? name.slice(0, -(extension.length + 1)) : name;
  const normalized = withoutExtension
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

  return normalized || 'attachment';
}

function buildStorageFileName(name: string) {
  const extension = getFileExtension(name);
  const safeExtension = extension || 'bin';
  return `${sanitizeFileStem(name)}-${crypto.randomUUID()}.${safeExtension}`;
}

async function validateTextFileContent(file: File) {
  const sample = await file.slice(0, 4096).arrayBuffer();
  const bytes = new Uint8Array(sample);

  if (bytes.some((byte) => byte === 0)) {
    throw new Error('Text attachments must be valid plain text files.');
  }
}

async function getValidatedAttachment(file: File) {
  if (!file) {
    throw new Error('Please choose a file to upload.');
  }

  if (file.size <= 0) {
    throw new Error('Attachments cannot be empty.');
  }

  if (file.size > SUPPORT_ATTACHMENT_MAX_SIZE) {
    throw new Error('Attachments must be 5MB or smaller.');
  }

  const mimeType = file.type.trim().toLowerCase();
  const rules = SUPPORT_ATTACHMENT_RULES[mimeType];
  if (!rules) {
    throw new Error('Only PNG, JPG, WEBP, GIF, TXT, MD, CSV, and JSON files are allowed.');
  }

  const extension = getFileExtension(file.name);
  if (!extension || !rules.extensions.includes(extension)) {
    throw new Error('The selected file extension does not match the allowed file type.');
  }

  if (rules.kind === 'text') {
    await validateTextFileContent(file);
  }

  return {
    fileName: file.name,
    mimeType,
    sizeBytes: file.size,
    kind: rules.kind,
  };
}

export async function prepareSupportAttachment(file: File): Promise<SupportAttachmentSelection> {
  const validated = await getValidatedAttachment(file);

  return {
    file,
    ...validated,
    previewUrl: validated.kind === 'image' ? URL.createObjectURL(file) : null,
  };
}

export async function uploadSupportAttachment(file: File, userId: string): Promise<SupportAttachmentUpload> {
  const validated = await getValidatedAttachment(file);
  const path = `${userId}/${buildStorageFileName(validated.fileName)}`;

  const { error } = await supabase.storage.from(SUPPORT_ATTACHMENT_BUCKET).upload(path, file, {
    upsert: false,
    cacheControl: '3600',
    contentType: validated.mimeType,
  });

  if (error) {
    throw new Error(error.message || 'Failed to upload the attachment.');
  }

  return {
    path,
    ...validated,
  };
}

export async function createSupportAttachmentSignedUrl(path: string, expiresInSeconds = 3600) {
  const { data, error } = await supabase.storage.from(SUPPORT_ATTACHMENT_BUCKET).createSignedUrl(path, expiresInSeconds);

  if (error) {
    throw new Error(error.message || 'Failed to open the attachment.');
  }

  return data.signedUrl;
}

export async function removeSupportAttachment(path: string) {
  const { error } = await supabase.storage.from(SUPPORT_ATTACHMENT_BUCKET).remove([path]);

  if (error) {
    throw new Error(error.message || 'Failed to remove the attachment.');
  }
}

export function revokeSupportAttachmentPreview(previewUrl?: string | null) {
  if (previewUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl);
  }
}

export function formatSupportAttachmentSize(sizeBytes: number) {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }

  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} KB`;
  }

  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
